import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, setPlayerToken, formatApiError } from "@/lib/api";
import { BrainTapLogo } from "@/components/Logo";
import AvatarMaker from "@/components/AvatarMaker";
import { toast } from "sonner";
import { ArrowRight, Loader2, Brain } from "lucide-react";

export default function Join() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(params.get("code") || "");
  const [checking, setChecking] = useState(false);
  const [room, setRoom] = useState(null);
  const [mode, setMode] = useState("quick");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [avatar, setAvatar] = useState({ kind: "knight" });
  const [submitting, setSubmitting] = useState(false);

  const codeClean = useMemo(() => code.replace(/\D/g, "").slice(0, 6), [code]);

  useEffect(() => {
    const prefill = params.get("code");
    if (prefill && /^\d{6}$/.test(prefill)) checkRoom(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkRoom(input) {
    const c = input.replace(/\D/g, "").slice(0, 6);
    if (c.length !== 6) {
      toast.error("Join codes are 6 digits");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get(`/venue/public/${c}`);
      setRoom(data.venue);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setChecking(false);
    }
  }

  async function playQuick() {
    if (name.trim().length < 2) {
      toast.error("Pick a table name (2+ characters)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/player/guest", { name: name.trim() });
      setPlayerToken(data.token);
      navigate(`/play/${room.code}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  }

  async function playFull() {
    if (name.trim().length < 2) {
      toast.error("Pick a table name (2+ characters)");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/player", {
        email, name: name.trim(), avatar: avatar,
      });
      setPlayerToken(data.token);
      navigate(`/play/${room.code}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-navy-900 px-4 py-10 text-cream"
      style={{ background: "radial-gradient(circle at 50% 0%, rgba(120,80,255,0.15), transparent 55%), radial-gradient(circle at 50% 100%, rgba(0,180,255,0.12), transparent 55%), #190a20" }}
      data-testid="join-page"
    >
      <Link to="/" className="mb-10 flex items-center gap-3">
        <BrainTapLogo lockupClass="h-24" />
      </Link>

      {!room ? (
        <div className="glass w-full max-w-md rounded-3xl p-8" data-testid="join-code-card">
          <p className="mb-2 font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-amber">Step 1 · The room</p>
          <h1 className="mb-7 font-display text-3xl font-extrabold uppercase tracking-tight">Enter the venue code</h1>
          <input
            data-testid="join-code-input"
            value={codeClean}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && checkRoom(codeClean)}
            placeholder="847291"
            inputMode="numeric"
            autoFocus
            className="font-mono-num w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-5 text-center text-4xl font-bold tracking-[0.35em] text-cream outline-none transition-colors focus:border-amber"
          />
          <button
            data-testid="join-code-submit"
            onClick={() => checkRoom(codeClean)}
            disabled={checking}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-display text-base font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_36px_rgba(245,179,53,0.4)] disabled:opacity-60"
          >
            {checking ? <Loader2 size={18} className="animate-spin" /> : <>Find my room <ArrowRight size={18} /></>}
          </button>
          <p className="mt-5 text-center text-sm text-mut">Scanned a QR? The code is already filled in — just tap.</p>
        </div>
      ) : (
        <div className="glass w-full max-w-md rounded-3xl p-8" data-testid="join-profile-card">
          <p className="mb-1 font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-amber">Step 2 · Your seat</p>
          <h1 className="mb-1 font-display text-3xl font-extrabold uppercase tracking-tight">{room.name}</h1>
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-mut">
            {room.region || "Local room"} · Code <span className="text-amber">{room.code}</span>
          </p>

          <div className="mb-6 grid grid-cols-2 gap-2" data-testid="join-mode-tabs">
            {[
              { key: "quick", label: "Quick play" },
              { key: "full", label: "Full player" },
            ].map((m) => (
              <button
                key={m.key}
                data-testid={`join-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`rounded-2xl border-2 p-3.5 text-left transition-all ${mode === m.key ? "border-amber bg-amber/10" : "border-ice/15 bg-navy-800/60 hover:border-ice/40"}`}
              >
                <div className="font-display text-sm font-extrabold uppercase tracking-wide text-cream">{m.label}</div>
                <div className="mt-1 text-[11px] leading-snug text-mut">
                  {m.key === "quick" ? "Just a name — play right now" : "Avatar maker, badges & prize spins"}
                </div>
              </button>
            ))}
          </div>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-ice/15" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-mut">or</span>
            <div className="h-px flex-1 bg-ice/15" />
          </div>

          <a
            data-testid="join-google-button"
            href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth?code=" + room.code)}`}
            className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-4 font-display text-base font-extrabold uppercase tracking-wide text-[#1f2937] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_36px_rgba(255,255,255,0.25)]"
          >
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.7-.4-3.9z"/></svg>
            Continue with Google
          </a>
          <p className="mt-3 text-center text-xs text-mut">One tap and you're in — full player with badges &amp; prize spins included.</p>

          <label className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut">Table name</label>
          <input
            data-testid="join-name-input"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 24))}
            placeholder="TriviaTitan"
            className="mb-5 w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 font-bold text-cream outline-none transition-colors focus:border-amber"
          />

          {mode === "quick" ? (
            <>
              <button
                data-testid="join-quick-button"
                onClick={playQuick}
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-display text-base font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_36px_rgba(245,179,53,0.4)] disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Take my seat <ArrowRight size={18} /></>}
              </button>
              <p className="mt-4 text-center text-xs text-mut">Quick players can't earn badges or prize spins — switch to Full player for that.</p>
            </>
          ) : (
            <>
              <label className="mb-2 block font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut">Email</label>
              <input
                data-testid="join-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@example.com"
                className="mb-5 w-full rounded-2xl border-2 border-ice/20 bg-navy-800/80 px-5 py-3.5 text-cream outline-none transition-colors focus:border-amber"
              />
              <div className="mb-4 flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-mut">
                <Brain size={13} className="text-amber" /> Pick your character
              </div>
              <div className="mb-6 max-h-[46vh] overflow-y-auto rounded-2xl border border-ice/10 bg-navy-900/60 p-4">
                <AvatarMaker config={avatar} onChange={setAvatar} />
              </div>
              <button
                data-testid="join-enter-button"
                onClick={playFull}
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-display text-base font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_36px_rgba(245,179,53,0.4)] disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Take my seat <ArrowRight size={18} /></>}
              </button>
            </>
          )}

          <button onClick={() => setRoom(null)} className="mt-4 w-full text-center font-mono text-[11px] uppercase tracking-[0.25em] text-mut transition-colors hover:text-amber">
            Wrong room? Change code
          </button>
        </div>
      )}
    </div>
  );
}
